/**
 * Metric data of the hub card, the metric detail and the slices (#2175 B2, B3).
 * Web counterpart of the hooks in mobile-management/src/features/metrics/useMetricData.ts:
 * the request plans and the figures come from `series.ts` (the ported pure
 * part); this file only runs the requests. Each request is one GET through
 * the generated client core, cached per organisation for five minutes under
 * the endpoint's own key (`lib/queryKeys.ts`), so cards, the detail and the
 * silos that ask the same question share one request.
 */

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { OpenAPI } from "@/api";
import { request as apiRequest } from "@/api/core/request";
import { useAuthContext } from "@/contexts/AuthContext";
import { useProjectionPoll } from "@/hooks/useProjectionPoll";
import { endpointKey, normaliseParams } from "@/lib/queryKeys";
import {
  deriveSeries,
  metricExtraParams,
  planSeries,
  planSlices,
  rankSlices,
  sliceTargets,
  type MetricRequest,
  type MetricScopeParams,
  type SliceMode,
  type SliceRow,
  type SliceTarget,
} from "./series";
import type { MetricDefinition } from "./types";

/** The key of one request: the endpoint's own key, shared with every other reader of it (#2193 B4). */
export function metricRequestKey(orgId: string | null | undefined, req: MetricRequest) {
  return endpointKey(orgId, req.path, req.params);
}

export interface MetricQueryState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** The oldest load time of the requests (the freshness rule), 0 before any. */
  dataUpdatedAt: number;
  refetch: () => Promise<unknown>;
}

function oldest(times: number[]): number {
  const known = times.filter((t) => t > 0);
  return known.length > 0 ? Math.min(...known) : 0;
}

/** Runs the requests; answers come back in request order (`undefined` until loaded). */
export function useMetricRequests(requests: MetricRequest[], enabled = true) {
  const { isSignedIn, isAuthReady, orgId } = useAuthContext();
  const active = enabled && isSignedIn && isAuthReady;
  const results = useQueries({
    queries: requests.map((req) => ({
      queryKey: metricRequestKey(orgId, req),
      // The signal aborts the request when no card reads it any more (#2191 D11).
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        apiRequest<unknown>(OpenAPI, {
          method: "GET",
          url: req.path,
          query: normaliseParams(req.path, req.params),
          signal,
        }),
      enabled: active,
      staleTime: 5 * 60 * 1000,
    })),
  });
  // A forecast answer without its projection (before the org's first run,
  // #2192) lacks the contracted months: it reads as loading and is re-polled,
  // never as a low figure. A pending update keeps the figures (#2191 B6).
  const poll = useProjectionPoll(active ? results : []);
  const pendingExpired = poll.missing && poll.expired;
  // Answers only while enabled, so a closed card never shows cached figures.
  const answers = results.map((r) => (active ? r.data : undefined));
  const state: MetricQueryState = {
    isLoading: active && (results.some((r) => r.isPending) || (poll.missing && !poll.expired)),
    isError: active && (results.some((r) => r.isError) || pendingExpired),
    error: results.find((r) => r.error)?.error ?? (pendingExpired ? new Error("forecast projection still pending") : null),
    dataUpdatedAt: oldest(results.map((r) => r.dataUpdatedAt)),
    refetch: () => Promise.all(results.map((r) => r.refetch())),
  };
  const stamp = results.map((r) => `${r.dataUpdatedAt}:${r.data === undefined ? 0 : 1}`).join("|");
  return { answers, state, stamp };
}

/**
 * One metric for a period and scope: the chart window (twelve months ending
 * at `to`), the period's total and delta, the sparkline.
 */
export function useMetricSeries(
  def: MetricDefinition,
  from: string,
  to: string,
  scope: MetricScopeParams,
  options: { enabled?: boolean } = {},
) {
  const plan = useMemo(
    () => planSeries(def, from, to, scope, metricExtraParams(def)),
    // The scope object is rebuilt by callers; its two fields are what counts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [def, from, to, scope.childcare_id, scope.group_id],
  );
  const { answers, state, stamp } = useMetricRequests(plan.all, options.enabled ?? true);
  const figures = useMemo(
    () => deriveSeries(def, plan, answers, from, to),
    // `stamp` changes whenever an answer does; the answers array is new on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [def, plan, from, to, stamp],
  );
  return { query: state, ...figures };
}

export interface MetricSlices {
  rows: SliceRow[];
  truncated: "top" | "first" | null;
  optionCount: number;
  /** The number of requests the fan-out sends (at most 24 per window). */
  requestCount: number;
  query: MetricQueryState;
}

/**
 * The metric sliced by location or group over the period: one request per
 * location or group (the endpoint's own filter), ranked by value.
 */
export function useMetricSlices(
  def: MetricDefinition,
  mode: SliceMode | null,
  from: string,
  to: string,
  options: {
    locations: readonly SliceTarget[];
    groups: readonly SliceTarget[];
    locationIds: readonly string[];
    allowedGroupIds: ReadonlySet<string> | null;
  },
): MetricSlices {
  const { locations, groups, locationIds, allowedGroupIds } = options;
  const targets = useMemo(
    () => sliceTargets(mode, locations, groups, { locationIds, allowedGroupIds }),
    [mode, locations, groups, locationIds, allowedGroupIds],
  );
  const plan = useMemo(
    () => planSlices(def, mode, targets, from, to, metricExtraParams(def)),
    [def, mode, targets, from, to],
  );
  const { answers, state, stamp } = useMetricRequests(plan.flat, mode !== null);
  const rows = useMemo(
    () => rankSlices(def, plan, answers, from, to),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [def, plan, from, to, stamp],
  );
  return {
    rows,
    truncated: plan.truncated,
    optionCount: plan.optionCount,
    requestCount: plan.flat.length,
    query: state,
  };
}
